import Backbone from 'backbone';
import Globalize from 'globalize';
import i18n from 'i18n';
import sprintf from 'sprintf';
import scriptjs from 'scriptjs';

// Loading Cultures need Globalize to be available on the Window object
window.Globalize = Globalize;

var Events = {
    ON_LOCAL_LOADED: 'LOCAL_LOADED',
    ON_PROPERTY_FILE_LOADED: 'PROPERTY_FILE_LOADED'
};

// Our globalization library is based off of .net but our number formats are based on java so
// we map the format key to the comparable .net format.
var dateFormatMap = {};
dateFormatMap['mmddyyyy'] = 'ddd, MMM d, yyyy';
dateFormatMap['ddmmyyyy'] = 'ddd, d MMM yyyy';
dateFormatMap['yyyymmdd'] = 'ddd, yyyy MMM d';

var shortDateFormatMap = {};
shortDateFormatMap['mmddyyyy'] = 'MMM d, yyyy';
shortDateFormatMap['ddmmyyyy'] = 'd MMM yyyy';
shortDateFormatMap['yyyymmdd'] = 'yyyy MMM d';

var shortDateMonthDayFormatMap = {};
shortDateMonthDayFormatMap['mmddyyyy'] = 'MMM d';
shortDateMonthDayFormatMap['ddmmyyyy'] = 'd MMM';
shortDateMonthDayFormatMap['yyyymmdd'] = 'MMM d';

var shortDateMonthYearFormatMap = {};
shortDateMonthYearFormatMap['mmddyyyy'] = 'MMM yyyy';
shortDateMonthYearFormatMap['ddmmyyyy'] = 'MMM yyyy';
shortDateMonthYearFormatMap['yyyymmdd'] = 'yyyy MMM';

var mediumDateFormatMap = {};
mediumDateFormatMap['mmddyyyy'] = 'MMMM d, yyyy';
mediumDateFormatMap['ddmmyyyy'] = 'd MMMM yyyy';
mediumDateFormatMap['yyyymmdd'] = 'yyyy MMMM d';

/**
 * Class which provides localization. Wraps a jQuery plugin which will fetch the corresponding properties
 * file from the server that us used to provide the correct translation.
 */
var Localizer = Backbone.Model.extend({
    currentDateFormatKey: 'mmddyyyy',

    supportedLocales: {
        ar: 'العربية',
        bg: 'Български',
        cs: 'Čeština',
        da: 'Dansk',
        de: 'Deutsch',
        en: 'English',
        es: 'Español',
        et: 'Eesti',
        fr: 'Français',
        hr: 'Hrvatski',
        it: 'Italiano',
        iw: 'עברית',
        lv: 'Latviešu',
        lt: 'Lietuvių',
        hu: 'Magyar',
        nl: 'Nederlands',
        no: 'Norsk',
        pl: 'Polski',
        pt_BR: 'Português (Brasil)',
        pt: 'Português',
        ru: 'Русский',
        ro: 'Română',
        sk: 'Slovenčina',
        sl: 'Slovenščina',
        fi: 'Suomi',
        sv: 'Svenska',
        vi: 'Tiếng Việt',
        tr: 'Türkçe',
        el: 'Ελληνικά',
        ja: '日本語',
        ko: '한국어',
        th: 'ไทย',
        in: 'Bahasa Indonesia',
        ms: 'Bahasa Melayu',
        zh_TW: '繁體中文',
        zh_CN: '简体中文',
        uk: 'Українська'
    },

    compoundLocales: {
        zh_TW: '繁體中文',
        zh_CN: '简体中文'
    },

    /**
     * This is a map that can be used to map one locale choice to another. Helpful when needing to map
     * a language to a particular locale.
     */
    alternateLocales: {
        zh: 'zh_CN' // If language is china then default to simplified Chinese.
    },

    /**
     * Initializes the jQuery internationalization plugin and sets the locale to the given value. May be
     * called repeatedly in order to change the language after the fact.
     *
     * @param locale to set as the current locale.
     */
    setLocale: function(locale) {
        locale = this.alternateLocales[locale] || locale;
        locale = locale.replace('-', '_');

        // The internationalization plugin will automatically resolve the correct properties file
        jQuery.i18n.properties({
            name: [],
            cache: this.urlBustValue,
            path: '',
            mode: 'map',
            language: locale
        });

        // Now we need to setup the Globalization plugin which handles date formatting.  This doesn't
        // have its own script loader so we need to do the loading on our own.
        var signalReady = false;
        if (locale == 'en' || locale == 'en_US') {
            // English culture is built in and is the default so no need to load anything.
            Globalize.culture('en');
            signalReady = true;
        } else if (this.isLocaleSupported(locale)) {
            // This will reduce the locale to the string that is supported. For example, es_US is not
            // supported but this will reduce the string to es which is.  zh_TW is supported so the
            // returned value is zh_TW.
            var supportedLocale = this.getPartialSupportedLocale(locale);

            supportedLocale = this.convertLocaleToGlobalizeFormat(locale); // plugin uses dashes instead of _

            // Fetch the culture file
            var _this = this;
            scriptjs([this.getCultureFilePath(supportedLocale)], function() {
                Globalize.culture(supportedLocale);
                _this.trigger(Events.ON_LOCAL_LOADED);
            });
        } else {
            // Resort to the default
            Globalize.culture('en');
            signalReady = true;
        }

        // Notify the UI that the culture is ready.
        if (signalReady) {
            this.trigger(Events.ON_LOCAL_LOADED);
        }
    },

    getLocale: function() {
        return Globalize.culture().name || 'en';
    },

    setUrlBustingParam: function(urlBustValue) {
        this.urlBustValue = urlBustValue;
    },

    setContextPath: function(contextPath) {
        this.contextPath = contextPath;
    },

    loadAdditionalData: function(map) {
        jQuery.i18n.loadAdditionalData(map);
    },

    /**
     * Loads the given property file.
     * @param path
     */
    loadPropertyFile: function(path, callback, context) {
        var fullPath = path;
        if (this.contextPath) {
            if (path.charAt(0) !== '/' && this.contextPath.charAt(this.contextPath.length - 1) !== '/') {
                fullPath = this.contextPath + '/' + path;
            } else if (path.charAt(0) === '/' && this.contextPath.charAt(this.contextPath.length - 1) === '/') {
                fullPath = this.contextPath + path.slice(1);
            } else {
                fullPath = this.contextPath + path;
            }
        }

        var settings = {
            async: true,
            success: function() {
                if (callback) {
                    callback.call(context, path);
                }
            }
        };

        jQuery.i18n.loadPropertyFiles(fullPath, settings);
    },

    /**
     * Given a key, provides the correct translation.
     *
     * @param keyName the key to retrieve.
     */
    localize: function(keyName) {
        return jQuery.i18n.prop.apply(jQuery.i18n.prop, arguments);
    },

    containsKey: function(keyName) {
        return jQuery.i18n.containsKey(keyName);
    },

    /**
     * Returns true if the given locale is supported.
     *
     * @param locale the locale to check.
     */
    isLocaleSupported: function(locale) {
        var localeSupported = this.getPartialSupportedLocale(locale);
        return !(localeSupported == null);
    },

    /**
     * We may support only the language of a locale (ex, en but not en_US). Given a locale string,
     * returns the locale that is actually supported. (ex. return en if en_US is not supported).
     *
     * @param locale the locale to process.
     */
    getPartialSupportedLocale: function(locale) {
        var lang = this.compoundLocales.hasOwnProperty(locale) ? locale : locale.split('_')[0];
        if (lang.length && this.supportedLocales.hasOwnProperty(lang)) {
            return lang;
        }
        return null;
    },

    /**
     *
     * @deprecated Use utils/date/DateParser.parseISOUTC(value) instead.
     */
    parseDateString: function(dateString) {
        // Expects YYYY-MM-DD which is how it is returned from the database
        var dateObj = Globalize.parseDate(dateString, 'yyyy-MM-dd');

        if (dateObj == null) {
            dateObj = Globalize.parseDate(dateString, 'yyyy-MM-dd HH:mm:ss');
        }

        return dateObj;
    },

    /**
     * Formats the given date object as a String.
     * @param theDate the Date object to format.
     * @param dateFormatString the format string to use for the formatting.
     * @deprecated Use DateFormatter instead
     */
    formatDate: function(theDate, dateFormatString) {
        var dateObj = theDate;
        if (typeof dateObj == 'string') {
            dateObj = this.parseDateString(theDate);
        }

        if (!dateFormatString) {
            dateFormatString = dateFormatMap[this.currentDateFormatKey];
        }

        return Globalize.format(dateObj, dateFormatString);
    },
    /**
     * Formats the given date object as a (short) String.
     * @param theDate the Date object to format.
     * @param dateFormatString the format string to use for the formatting.
     */
    formatShortDate: function(theDate, dateFormatString) {
        var dateObj = theDate;
        if (typeof dateObj == 'string') {
            dateObj = this.parseDateString(theDate);
        }

        if (!dateFormatString) {
            dateFormatString = shortDateFormatMap[this.currentDateFormatKey];
        }

        return Globalize.format(dateObj, dateFormatString);
    },
    /**
     * Formats the given date object as a (short) String without the year.
     * @param theDate the Date object to format.
     * @param dateFormatString the format string to use for the formatting.
     */
    formatShortDateNoYear: function(theDate, dateFormatString) {
        var dateObj = theDate;
        if (typeof dateObj == 'string') {
            dateObj = this.parseDateString(theDate);
        }

        if (!dateFormatString) {
            dateFormatString = shortDateMonthDayFormatMap[this.currentDateFormatKey];
        }

        return Globalize.format(dateObj, dateFormatString);
    },

    /**
     * Formats the given date object as a (short) String without the year.
     * @param theDate the Date object to format.
     * @param dateFormatString the format string to use for the formatting.
     */
    formatShortDateMonthAndYear: function(theDate, dateFormatString) {
        var dateObj = theDate;
        if (typeof dateObj == 'string') {
            dateObj = this.parseDateString(theDate);
        }

        if (!dateFormatString) {
            dateFormatString = shortDateMonthYearFormatMap[this.currentDateFormatKey];
        }

        return Globalize.format(dateObj, dateFormatString);
    },

    /**
     * Formats the given date object as a (medium) String.
     * @param theDate the Date object to format.
     * @param dateFormatString the format string to use for the formatting.
     */
    formatMediumDate: function(theDate, dateFormatString) {
        var dateObj = theDate;
        if (typeof dateObj == 'string') {
            dateObj = this.parseDateString(theDate);
        }

        if (!dateFormatString) {
            dateFormatString = mediumDateFormatMap[this.currentDateFormatKey];
        }

        return Globalize.format(dateObj, dateFormatString);
    },

    /**
     * Returns the path to culture file.
     * @param locale
     */
    getCultureFilePath: function(locale) {
        return '/web-react/js/cultures/globalize.culture.' + locale + '.js';
    },

    /**
     * Converts the locale to the format required by the globalization plugin.
     * @param locale
     */
    convertLocaleToGlobalizeFormat: function(locale) {
        return locale.replace('_', '-');
    },

    /**
     * Sets the key that should be used for formatting dates. This will be month day vs day month.
     * @param dateFormatKey
     */
    setCurrentDateFormatKey: function(dateFormatKey) {
        this.currentDateFormatKey = dateFormatKey;
    },

    format: function() {
        return sprintf.apply(this, arguments);
    },

    mapSupportedLocales: function(locale) {
        return this.supportedLocales[locale];
    }
});

Localizer = new Localizer();
Localizer.Events = Events;

export default Localizer;
